// Stanford University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Stanford's Common Data Set 2025-2026 (Institutional Research & Decision
//    Support): C1 applicants/admits/enrollees for Fall 2025, C8 test policy
//    for Fall 2027, C21/C22 plans. Quirk: IRDS hosts the PDF on Google Drive;
//    the source link is the IRDS Common Data Set page that links to it. The
//    C1 residency table gives totals only, so there are no residency rows.
//  - admission.stanford.edu: First-Year Applicants (requirements and
//    deadlines, updated September 10, 2026), Regular Decision and Restrictive
//    Early Action (the REA policy), Standardized Testing. None of the pages
//    names an entry year; they are the pages posted for the application that
//    opened August 2026 (Fall 2027 entry), and the CDS C8 names fall 2027.
//  - Stanford Financial Aid, The Student Budget: the 2026-2027 cost of
//    attendance, item by item. The CDS G5 gives a lower other-expenses figure
//    and no transportation; the financial aid office's budget is used here.
//  - College Scorecard API, school id 243744 (net price by family income).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Stanford Common Data Set 2025-2026',
        url: 'https://irds.stanford.edu/data-findings/cds',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Stanford Admission, First-Year Applicants: Requirements and Deadlines',
        url: 'https://admission.stanford.edu/apply/first-year/index.html',
        read: '2026-10-01',
    },
    rea: {
        label: 'Stanford Admission, Regular Decision and Restrictive Early Action',
        url: 'https://admission.stanford.edu/apply/first-year/decision_process.html',
        read: '2026-10-01',
    },
    testing: {
        label: 'Stanford Admission, Standardized Testing',
        url: 'https://admission.stanford.edu/apply/first-year/testing.html',
        read: '2026-10-01',
    },
    budget: {
        label: 'Stanford Financial Aid, The Student Budget',
        url: 'https://financialaid.stanford.edu/undergrad/budget/index.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?243744-Stanford-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'stanford-university',
    kbId: 'stanford_university',
    name: 'Stanford University',
    shortName: 'Stanford',
    location: 'Stanford, CA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Stanford identity guide); decoration only.
    branding: {
        primary: '#8C1515',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/stanford_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 60646,
        admitted: 2302,
        enrolled: 1839,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Stanford\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Restrictive early action',
        note: 'Stanford has no early decision plan. The Common Data Set lists its Restrictive Early Action dates but gives no early action counts, so it gives no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Restrictive Early Action', deadline: 'November 1', decision: 'By mid-December', binding: false },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'By early April', binding: false },
        ],
        note: 'Applications close at 11:59 pm in your local time zone. If you send the optional Arts Portfolio, the Common App is due earlier: October 15 for Restrictive Early Action and December 5 for Regular Decision. The reply date is May 1.',
        source: 'firstYear',
        binding: 'Restrictive Early Action is not binding: if you are admitted, you have until May 1 to respond. While you apply this way, you may not apply early to any other private college, or to a public university under a binding plan such as early decision. If you are not admitted, that decision is final and you may not reapply in Regular Decision. If you are deferred, you get a final decision by early April.',
        bindingSource: 'rea',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Stanford requires the ACT or SAT. You self-report your highest scores in the application, and official scores are needed only if you enroll. There is no minimum score and no preferred test.',
        source: 'testing',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 67731 },
            { item: 'Housing and food', amount: 22944 },
            { item: 'Student fees allowance', amount: 2610 },
            { item: 'Books and supplies allowance', amount: 855 },
            { item: 'Personal expenses allowance', amount: 3405 },
            { item: 'Travel', amount: null },
        ],
        total: 97545,
        note: 'Travel varies by where you live, so it is not in the $97,545 total. New students also pay a one-time $525 orientation fee and a $250 document fee, which Stanford adds to a first-year budget, so a first year costs $775 more.',
        source: 'budget',
        netPrice: {
            average: 12307,
            byIncome: [
                { income: '$0 to $30,000', amount: 1067 },
                { income: '$30,001 to $48,000', amount: null },
                { income: '$48,001 to $75,000', amount: null },
                { income: '$75,001 to $110,000', amount: 6603 },
                { income: 'Over $110,000', amount: 47575 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the $30,001 to $48,000 and $48,001 to $75,000 bands. Your own price comes from Stanford\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
